import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments, faClipboardList, faPrint, faTh } from "@fortawesome/free-solid-svg-icons";
import { PrismBanner, PrismButton, PrismLoading, PrismSelect } from "@prism-ui/react";
import { getProjectFairDetails } from "../../services/projectFairDetails.js";
import PermissionLevel from "../../data/PermissionLevel";
import ProjectCard from "./ProjectCard";
import ProjectEditorModal from "./ProjectEditorModal";
import { ViewToggle } from "./ViewToggle";
import { FilterBar } from "./FilterBar";
import { SortControl } from "./SortControl";
import { applyPipeline } from "./projects.utils";
import type { Project, SortMode, ViewMode } from "./projects.utils";
import { useDebouncedValue } from "./useDebouncedValue";
import { useProgressiveList } from "./useProgressiveList";
import { isWindowLive, useNow } from "../../util/fairVisibility";
import ProjectFairPrint from "./ProjectFairPrint";
import "./Projects.css";

const TagCloudView = lazy(() => import("./TagCloudView"));

// The single-fair page intentionally omits the Timeline view: with only one year of data a
// timeline is a single column of dots — no meaningful history to explore. Classic, Compact,
// and Tag Cloud are the useful modes here.
const FAIR_VIEWS: ViewMode[] = ["classic", "compact", "tagcloud"];

interface ProjectFairProps {
  currentUser?: {
    user_id?: number | string;
    chapter_id?: number;
    Role?: { permission_level: number };
    User?: { projects_view?: ViewMode };
  };
  params: { year: string };
  /**
   * When provided, renders this data directly instead of fetching from the server. Used by
   * the admin Project Fairs "Preview" modal to show unsaved draft edits on the real public
   * layout without persisting anything or hitting the network.
   */
  previewOverride?: FairDetails;
}

export interface FairDetails {
  id?: number;
  chapter_id?: number;
  chapter_key?: string;
  Chapter?: { id: number; location: string };
  Chapters?: Array<{ id: number; location: string }>;
  events?: FairDetails[];
  title?: string;
  friendly_date?: string;
  friendly_time?: string;
  friendly_time_2?: string;
  welcome_msg?: string | null;
  closing_msg?: string | null;
  directions?: string;
  survey_link?: string;
  survey_text?: string;
  support_channel_link?: string;
  support_channel_name?: string;
  end_date_sec?: number;
  print_enabled?: boolean;
  join_live_enabled?: boolean;
  video_stream_name?: string;
  video_stream_link?: string;
  spark_hub_name?: string;
  spark_hub_link?: string;
  link_visibility?: {
    joinChannel?: LinkWindow;
    videoStream?: LinkWindow;
    sparkHub?: LinkWindow;
    pageActive?: LinkWindow;
  };
  ApprovedProjects?: Project[];
  projects?: Project[];
}

type LinkWindow = {
  fromUtc?: string | null;
  toUtc?: string | null;
  timezone?: string | null;
};

// Officers routinely enter chapter links without a scheme ("www.google.com", "teams.
// microsoft.com/..."). The browser reads a scheme-less href as a path relative to the
// current page, so the link resolves under the portal instead of leaving it. Anything that
// already carries a scheme (including mailto:) or is deliberately site-relative is left
// untouched.
export const toExternalHref = (link?: string | null): string => {
  const trimmed = (link || "").trim();
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || trimmed.startsWith("/")) return trimmed;
  return `https://${trimmed}`;
};

const ProjectFair = ({ currentUser, params, previewOverride }: ProjectFairProps) => {
  const fairYear = params.year;
  const printContentRef = useRef<HTMLDivElement | null>(null);

  const [fairDetails, setFairDetails] = useState<FairDetails>({ projects: [] });
  const [eventOptions, setEventOptions] = useState<Array<{ key: string; label: string; description: string }>>([]);
  // Empty unless the URL already carried an anchor (a deep link, or a prior explicit
  // choice) — the page never infers a chapter for the visitor. `hasChosenEvent` below is
  // derived from this, not tracked separately, so "no selection yet" always means one
  // consistent thing: an empty key.
  const [selectedChapterKey, setSelectedChapterKey] = useState<string>(() => {
    const raw = window.location.hash.replace(/^#/, "").trim().toLowerCase();
    return raw;
  });
  const [projects, setProjects] = useState<Project[]>([]);
  // A ticking clock so every time-gated tile below opens and closes on its own. Attendees
  // habitually land on the page a few minutes early; without this they would have to guess
  // that a reload is what makes the Join buttons appear.
  const now = useNow();

  // Whether the fair is live right now. Derived from the pageActive visibility window, which
  // is the authoritative signal the server also uses (see projectFairController's
  // serializeEvent) — the legacy `end_date_sec` column is never populated for records created
  // through the admin pane, which left this permanently false and hid the directions.
  const active = useMemo(
    () => isWindowLive(fairDetails.link_visibility?.pageActive, now),
    [fairDetails.link_visibility, now]
  );

  // "Over" is narrower than "not active": a fair scheduled for next month is also not
  // active, and telling those visitors to look out for next year would be wrong. Only a
  // closing bound that has actually passed means the fair has ended.
  const fairIsOver = useMemo(() => {
    const closesAt = fairDetails.link_visibility?.pageActive?.toUtc;
    if (!closesAt) return false;
    const closesMs = new Date(closesAt).getTime();
    return !Number.isNaN(closesMs) && now > closesMs;
  }, [fairDetails.link_visibility, now]);

  const [showAddProjectDialog, setShowAddProjectDialog] = useState(false);
  const [seed] = useState(() => Date.now());
  const handlePrint = useReactToPrint({
    contentRef: printContentRef,
    pageStyle: "@page { size: letter landscape; margin: 9mm; }",
  });

  const initialView = currentUser?.User?.projects_view;
  const [viewMode, setViewMode] = useState<ViewMode>(
    initialView && FAIR_VIEWS.includes(initialView) ? initialView : "classic"
  );

  const [selectedYears, setSelectedYears] = useState<Array<number | string>>([String(fairYear)]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortMode>("random");

  const isOfficer = (currentUser?.Role?.permission_level ?? 0) >= PermissionLevel.Officer;
  const canSeeHiddenProjects = !!currentUser;

  useEffect(() => {
    const handleHashChange = () => {
      const raw = window.location.hash.replace(/^#/, "").trim().toLowerCase();
      setSelectedChapterKey(raw);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const refreshFairDetails = async (chapterOverride?: string) => {
    const targetChapter = chapterOverride ?? selectedChapterKey;
    const details: FairDetails = await getProjectFairDetails(fairYear, targetChapter || undefined);
    const events = (details.events || []).map((event) => {
      // Two events owned by the same chapter both render as "West", which tells a visitor
      // nothing about which is which. The anchor is the one value that is unique per event
      // and is what the URL shows, so it is the label.
      const slug = event.chapter_key || "";
      const location = event.Chapter?.location || `Chapter ${event.chapter_id}`;
      // Second line for the landing picker only, where nothing else on screen says when an
      // event is. Owners are named only for co-owned events; a single owner adds nothing.
      const owners = (event.Chapters || []).map((c) => c.location).filter(Boolean);
      const description = [event.friendly_date, owners.length > 1 ? owners.join(" & ") : ""]
        .filter(Boolean)
        .join(" · ");
      return { key: slug, label: slug ? `#${slug}` : location, description };
    });
    setEventOptions(events);

    // No event has been explicitly chosen yet — a fresh landing on the bare year URL. Every
    // visitor gets the same neutral prompt to pick an event; the response is only used to
    // populate that picker, never to silently drop someone into whichever chapter happens
    // to be live right now.
    if (!targetChapter) {
      setFairDetails({ projects: [] });
      setProjects([]);
      return;
    }

    const approvedRaw = details.ApprovedProjects || [];
    const approved = canSeeHiddenProjects
      ? approvedRaw
      : approvedRaw.filter((project) => project.active !== false);
    setFairDetails({ ...details, projects: approved });
    setProjects(approved);
    if (details.chapter_key && details.chapter_key !== targetChapter) {
      setSelectedChapterKey(details.chapter_key);
      window.history.replaceState(null, "", `${window.location.pathname}#${details.chapter_key}`);
    }
  };

  useEffect(() => {
    // Preview mode (admin "Preview" modal): render the supplied draft data directly and
    // skip the network fetch entirely — nothing here is persisted or read from the server.
    if (previewOverride) {
      const approvedRaw = previewOverride.ApprovedProjects || [];
      const approved = canSeeHiddenProjects
        ? approvedRaw
        : approvedRaw.filter((project) => project.active !== false);
      setFairDetails({ ...previewOverride, projects: approved });
      setEventOptions([
        {
          key: previewOverride.chapter_key || "",
          label: previewOverride.Chapter?.location || previewOverride.chapter_key || "Preview",
          description: "",
        },
      ]);
      setProjects(approved);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await refreshFairDetails();
      } catch (error) {
        console.error("Failed to load project fair details:", error);
        if (!cancelled) {
          setFairDetails({ projects: [] });
          setEventOptions([]);
          setProjects([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fairYear, selectedChapterKey, canSeeHiddenProjects, previewOverride]);

  const availableYears = useMemo(
    () => [...new Set(projects.map((p) => p.year))].sort((a, b) => Number(b) - Number(a)),
    [projects]
  );
  const availableTags = useMemo(
    () => [...new Set(projects.flatMap((p) => (p.tags ?? []).map((t) => t.tag.name)))].sort(),
    [projects]
  );
  const availableLocations = useMemo(
    () => [...new Set(projects.map((p) => p.Chapter?.location).filter(Boolean) as string[])].sort(),
    [projects]
  );

  const debouncedSearch = useDebouncedValue(search, 200);

  const filtered = useMemo(
    () =>
      applyPipeline(projects, {
        years: selectedYears,
        tags: selectedTags,
        locations: selectedLocations,
        search: debouncedSearch,
        sort,
        seed,
      }),
    [projects, selectedYears, selectedTags, selectedLocations, debouncedSearch, sort, seed]
  );

  const { visible } = useProgressiveList(filtered, { resetKey: viewMode });

  // Tab switches are ephemeral navigation only — they never overwrite the user's saved
  // default projects view. The default is chosen explicitly on the /projects page.
  const handleTagCrossFilter = (tag: string) => {
    setSelectedTags([tag]);
    setViewMode("classic");
  };

  const openAddProjectDialog = () => {
    if (!isOfficer || !fairDetails.id) return;
    setShowAddProjectDialog(true);
  };

  const handleProjectAdded = async () => {
    setShowAddProjectDialog(false);
    try {
      await refreshFairDetails(selectedChapterKey || undefined);
    } catch (error) {
      console.error("Failed to refresh project fair details after adding project:", error);
    }
  };

  const paragraphs = (text?: string | null, prefix = "p") =>
    text
      ? text.split("\n").map((line, i) => (
          <p key={`${prefix}-${i}`} className="intro">
            {line}
          </p>
        ))
      : null;

  // "Join Live" means a Teams stream exists for this event. In-person events reuse the same
  // projects as the virtual fair, so without this per-event switch an attendee on an
  // in-person page sees a Join Live button for a meeting that does not exist. Older events
  // predate the column and must keep working, so an absent value reads as enabled.
  const joinLiveEnabled = fairDetails.join_live_enabled !== false;

  const cardList = (collapsed: boolean) =>
    filtered.length > 0 ? (
      <ul className="project-list">
        {visible.map((p) => (
          <li key={p.id} className="project">
            <ProjectCard
              data={p}
              editable={isOfficer && !previewOverride}
              collapsed={collapsed}
              live={active}
              joinLiveEnabled={joinLiveEnabled}
            />
          </li>
        ))}
      </ul>
    ) : (
      <section className="project-fair-text clr:text-center">
        <div>
          <p>
            <FontAwesomeIcon icon={faTh} size="6x" className="clr:text-secondary-base text-faded-25" />
          </p>
          <p className="clr:text-secondary-base">
            {projects.length > 0 ? "No Results Found!" : "Project list coming soon!"}
          </p>
        </div>
      </section>
    );

  // Nothing has been explicitly chosen yet (no anchor in the URL, no prior picker
  // selection) — every visitor gets the same neutral, exploratory landing state instead of
  // being dropped into whichever chapter's event happens to be live. The event picker is
  // the only thing on screen; picking an option is what appends the `#chapter-key` anchor
  // and loads that event's details (see the picker's onPrismChange below).
  if (!previewOverride && !selectedChapterKey) {
    return (
      <main className="project-fair">
        <section className="project-fair-header">
          <h1 className="section-title">Project Fair {fairYear}</h1>
          <p className="project-fair-landing-prompt">
            Choose an event below to see its projects, schedule, and links.
          </p>
          {eventOptions.length > 0 ? (
            <div className="project-fair-chapter-picker no-print">
              <PrismSelect
                label="Events"
                as="custom"
                value=""
                options={[
                  { value: "", display: "Select an event…" },
                  ...eventOptions.map((event) => ({
                    value: event.key,
                    display: event.label,
                    ...(event.description ? { description: event.description } : {}),
                  })),
                ]}
                onPrismChange={(e: CustomEvent<{ value?: string }>) => {
                  const next = e.detail?.value || "";
                  if (!next) return;
                  setSelectedChapterKey(next);
                  window.history.replaceState(null, "", `${window.location.pathname}#${next}`);
                }}
              />
            </div>
          ) : (
            <p className="clr:text-secondary-base">Project list coming soon!</p>
          )}
        </section>
      </main>
    );
  }

  return (
    <>
      <main className="project-fair">
        <div className="project-fair-toolbar no-print">
          {isOfficer && !previewOverride ? (
            <>
              <PrismButton
                className="project-fair-print-button"
                setAriaLabel="Add project to fair"
                display="ghost-icon"
                tone="primary-brand"
                onPrismClick={openAddProjectDialog}
              >
                <span aria-hidden="true">＋</span>
              </PrismButton>
              {fairDetails.print_enabled !== false ? (
                <PrismButton
                  className="project-fair-print-button"
                  setAriaLabel="Print project fair"
                  display="ghost-icon"
                  tone="primary-brand"
                  onPrismClick={() => handlePrint()}
                >
                  <FontAwesomeIcon icon={faPrint} />
                </PrismButton>
              ) : null}
            </>
          ) : null}
        </div>

        <section className="project-fair-header">
          <h1 className="section-title">{fairDetails.title}</h1>
          <h2>{fairDetails.friendly_date}</h2>
          <h2>{fairDetails.friendly_time}</h2>
          {fairDetails.friendly_time_2 ? <h2>{fairDetails.friendly_time_2}</h2> : null}

          {fairIsOver ? (
            <PrismBanner tone="info" className="project-fair-complete">
              <strong>The Project Fair is over!</strong>
              Thank you to all attendees and participants this year! Be sure to look out for
              communication about joining CLEAR next year!
            </PrismBanner>
          ) : null}

          {eventOptions.length > 1 ? (
          <div className="project-fair-chapter-picker no-print">
            <PrismSelect
              label="Events"
              value={selectedChapterKey}
              options={eventOptions.map((event) => ({ value: event.key, display: event.label }))}
              onPrismChange={(e: CustomEvent<{ value?: string }>) => {
                const next = e.detail?.value || "";
                setSelectedChapterKey(next);
                window.history.replaceState(null, "", `${window.location.pathname}${next ? `#${next}` : ""}`);
              }}
            />
          </div>
          ) : null}
        </section>

        {active && fairDetails.directions ? (
          <section className="project-fair-text project-fair-directions">{paragraphs(fairDetails.directions, "dir")}</section>
        ) : null}
        {fairDetails.welcome_msg ? (
          <section className="project-fair-text">{paragraphs(fairDetails.welcome_msg, "intro")}</section>
        ) : null}

        <section className="project-fair-controls no-print">
          <div className="projects project-fair-controls-inner">
            <ViewToggle value={viewMode} onChange={setViewMode} modes={FAIR_VIEWS} />
            <div className="project-fair-controls-row">
              <FilterBar
                availableYears={availableYears}
                availableTags={availableTags}
                availableLocations={availableLocations}
                selectedYears={selectedYears}
                selectedTags={selectedTags}
                selectedLocations={selectedLocations}
                search={search}
                lockedYear={fairYear}
                hideYear
                onYearsChange={setSelectedYears}
                onTagsChange={setSelectedTags}
                onLocationsChange={setSelectedLocations}
                onSearchChange={setSearch}
                onClear={() => {
                  // Keep the fair's year pinned; clear everything the user added.
                  setSelectedYears([String(fairYear)]);
                  setSelectedTags([]);
                  setSelectedLocations([]);
                  setSearch("");
                }}
                trailing={
                  viewMode === "classic" || viewMode === "compact" ? (
                    <SortControl value={sort} onChange={setSort} />
                  ) : null
                }
              />
            </div>
          </div>
        </section>

        <Suspense fallback={<PrismLoading />}>
          {viewMode === "classic" && cardList(false)}
          {viewMode === "compact" && cardList(true)}
          {viewMode === "tagcloud" && (
            <TagCloudView projects={filtered} onSelectTag={handleTagCrossFilter} />
          )}
        </Suspense>

        {fairDetails.support_channel_link || fairDetails.closing_msg || (fairDetails.survey_link && fairDetails.survey_text) ? (
          <section className="project-fair-closing-banner">
            {fairDetails.support_channel_link ? (
              <div className="project-fair-closing-tile">
                <FontAwesomeIcon icon={faComments} size="2x" />
                <h4>Join Our Channel</h4>
                <p>
                  Need help getting into an invite? Join the{" "}
                  <a href={toExternalHref(fairDetails.support_channel_link)} target="_blank" rel="noopener noreferrer">
                    {fairDetails.support_channel_name || "Project Fair"}
                  </a>{" "}
                  channel.
                </p>
              </div>
            ) : null}
            {fairDetails.closing_msg ? (
              <div className="project-fair-closing-tile">
                <h4>Thank You</h4>
                {paragraphs(fairDetails.closing_msg, "closing")}
              </div>
            ) : null}
            {fairDetails.survey_link && fairDetails.survey_text ? (
              <div className="project-fair-closing-tile">
                <FontAwesomeIcon icon={faClipboardList} size="2x" />
                <h4>Provide Feedback</h4>
                <a href={toExternalHref(fairDetails.survey_link)} target="_blank" rel="noopener noreferrer">
                  {fairDetails.survey_text}
                </a>
              </div>
            ) : null}
          </section>
        ) : null}
      </main>

      {/* Printable content (hidden on screen) */}
      <ProjectEditorModal
        open={showAddProjectDialog}
        mode="create"
        eventId={fairDetails.id}
        currentUser={currentUser}
        onClose={() => setShowAddProjectDialog(false)}
        onSaved={handleProjectAdded}
      />

      <div style={{ display: "none" }}>
        <ProjectFairPrint
          ref={printContentRef}
          fairDetails={fairDetails}
          projects={filtered.length ? filtered : projects}
          currentUser={currentUser}
        />
      </div>
    </>
  );
};

export default ProjectFair;
